/**
 * CARGO CLIMB — fourth cartridge, single-screen platformer (MVP).
 *
 * Heritage: Donkey Kong (1981) — scaffolding climb + dodge falling crates
 * + reach the top, plus Burger Time (1982) — multi-tier platforms with
 * ladders and the occasional ride-the-piece-down shortcut. Original
 * mechanic: **mid-air crate catch** — jump UP into a falling crate and
 * latch on; the crate then carries the player to whichever platform it
 * lands on. Risky shortcut (you can't bail) but a fast lateral move
 * down through the scaffolding.
 *
 * Layout: 480x270 canvas. The playfield is a centered 270-wide portrait
 * column (per the cabinet's landscape-only rule); the left + right
 * gutters are cartridge-owned chrome (HUD on the left, scaffolding +
 * crane silhouette on the right). NOT black bars.
 *
 * Determinism: all randomness routes through ctx.random.
 */

import type {
  GameContext,
  GameInstance,
  InputState,
} from '@arcraz/arcadia-types';
import {
  CRANE_BODY,
  CRANE_CABLE,
  CRANE_LIGHT_OFF,
  CRANE_LIGHT_ON,
  CRATE_CARRIED_OUTLINE,
  CRATE_L,
  CRATE_L_DARK,
  CRATE_LONG,
  CRATE_LONG_DARK,
  CRATE_SQUARE,
  CRATE_SQUARE_DARK,
  FIELD_BG,
  FLASH_HIT,
  GUTTER_BG,
  HUD_ALERT,
  HUD_LABEL,
  HUD_LEVEL,
  HUD_LIVES,
  HUD_SCORE,
  LADDER,
  LADDER_RUNG,
  PLAYER_BODY,
  PLAYER_HEAD,
  PLAYER_LAND_DUST,
  PLAYER_OUTLINE,
  SCAFFOLD,
  SCAFFOLD_DARK,
} from './palette.js';

// --- Canvas + playfield -----------------------------------------------
const WIDTH = 480;
const HEIGHT = 270;
const PLAY_W = 270;
const PLAY_X0 = (WIDTH - PLAY_W) / 2; // 105
const PLAY_X1 = PLAY_X0 + PLAY_W; // 375

// --- Platforms --------------------------------------------------------
// 6 rows. Row 0 = bottom (player start), Row 5 = top (crane / win).
const ROWS = 6;
const ROW_GAP = 45; // 270 / 6
const PLATFORM_THICKNESS = 5;
// y-coordinate of the TOP of each platform's surface (where the player stands).
// Bottom row sits at y=265, top row at y=40 (leaving room for the crane).
function platformY(row: number): number {
  return HEIGHT - 5 - row * ROW_GAP;
}

// --- Ladders ----------------------------------------------------------
// 5 ladders connect adjacent rows (0-1, 1-2, ..., 4-5). Alternate sides
// so the climb forces a horizontal traverse on each row.
const LADDER_W = 12;
// Ladder x-center per row. Even rows: left side. Odd rows: right side.
function ladderX(rowFrom: number): number {
  // rowFrom 0..4 — ladder connects rowFrom up to rowFrom+1.
  // Stagger between left-third and right-third of the playfield.
  const leftCenter = PLAY_X0 + 40;
  const rightCenter = PLAY_X1 - 40;
  return rowFrom % 2 === 0 ? leftCenter : rightCenter;
}

// --- Player -----------------------------------------------------------
const PLAYER_W = 10;
const PLAYER_H = 14;
const PLAYER_WALK_SPEED = 1.0;
const PLAYER_CLIMB_SPEED = 0.9;
const PLAYER_JUMP_VY = -3.6;
const GRAVITY = 0.22;
const PLAYER_LAND_DUST_TICKS = 8;

// --- Crates -----------------------------------------------------------
// Tier 0 = square (small, predictable). Tier 1 = long (wider hitbox).
// Tier 2 = L-shape (most chaotic — can re-roll horizontal direction at
// platform edges).
type CrateTier = 0 | 1 | 2;
const CRATE_W = [12, 22, 16] as const;
const CRATE_H = [12, 8, 16] as const;
const CRATE_SCORE = [10, 20, 50] as const;
const CRATE_BOUNCE_VY = [-1.8, -1.4, -2.2] as const; // first bounce off platform top
const CRATE_BOUNCE_DAMPING = 0.78; // each subsequent bounce loses 22% vertical energy
const CRATE_MIN_BOUNCE_VY = -0.4; // below this, no more bounce — crate falls through
const CRATE_FALL_GRAVITY = 0.16;
const CRATE_MAX_VY = 3.2;
const CRATE_HORIZONTAL_VX = [0.6, 0.5, 0.8] as const;

// --- Crane / wave -----------------------------------------------------
// Crane sits above the top platform. Spawns crates from a random x within
// the playfield. Spawn cadence shortens as level rises.
const CRANE_BASE_INTERVAL = 75; // ticks between spawns at level 1
const CRANE_MIN_INTERVAL = 28; // floor at which it caps out
const CRANE_INTERVAL_LEVEL_DROP = 6; // each level shaves this many ticks
const CRANE_WARN_TICKS = 18; // crane-light blinks this long before spawn

// --- Lives / progression ----------------------------------------------
const LIVES_START = 3;
const RESPAWN_INVULN = 60;
const CRASH_FLASH_TICKS = 22;

// --- Mid-air crate catch ----------------------------------------------
// Player only latches if they're moving UP (vy < 0) and the player's top
// edge crosses the crate's bottom. Once latched, player rides the crate
// to its next platform landing.
const CATCH_LATCH_VY_MAX = -0.5;

type Phase = 'playing' | 'crashed' | 'level-up' | 'game-over';

interface Vec {
  x: number;
  y: number;
}

interface Player extends Vec {
  vx: number;
  vy: number;
  onGround: boolean;
  onLadder: boolean;
  facing: -1 | 1;
  invuln: number;
  landDust: number;
  /** When riding a crate, the crate instance is referenced here. While
   *  riding, the player's input only matters for "bail at landing". */
  ridingCrate: Crate | null;
}

interface Crate extends Vec {
  vx: number;
  vy: number;
  tier: CrateTier;
  /** Has the player already scored this crate? (Prevent double-score
   *  on a single dodge.) */
  scored: boolean;
  /** Tick at spawn — used to age out crates that have bounced too long
   *  without hitting anything (rare but covers stuck-state). */
  born: number;
  /** Bounce count — multiplied into CRATE_BOUNCE_DAMPING so each bounce
   *  loses energy. Once the next bounce would produce a vy weaker than
   *  CRATE_MIN_BOUNCE_VY, the crate falls through and exits the bottom. */
  bounces: number;
  /** Width / height accessors. Constant per crate but kept as functions
   *  so the tier->dimensions lookup happens once at construction and is
   *  closed over in the makeCrate factory. */
  w: () => number;
  h: () => number;
}

export class CargoClimbInstance implements GameInstance {
  private readonly ctx: GameContext;

  private tickCount = 0;
  private phase: Phase = 'playing';
  private crashFlash = 0;
  private levelUpTicks = 0;

  private score = 0;
  private lives = LIVES_START;
  private level = 1;
  private finalScore = 0;
  private finalLevel = 1;

  private player: Player;
  private crates: Crate[] = [];
  private craneSpawnTimer = 0;
  private craneWarnTimer = 0;
  /** Next crate tier in the queue — used by the HUD "next crate" indicator. */
  private nextCrateTier: CrateTier = 0;

  private restartArmed = false;

  constructor(ctx: GameContext) {
    this.ctx = ctx;
    this.player = this.makePlayer();
    this.rollNextCrateTier();
    this.craneSpawnTimer = CRANE_BASE_INTERVAL;
  }

  // -- GameInstance contract --------------------------------------------

  input(state: InputState): void {
    if (this.phase === 'game-over') {
      if (state.justPressed.has('fireA') || state.justPressed.has('start')) {
        this.restartArmed = true;
      }
      return;
    }

    // While riding a crate, player input is suppressed (you committed to
    // the ride). Only fireA/start "bail" on landing (handled in update).
    if (this.player.ridingCrate) return;
    if (this.phase !== 'playing') return;

    const p = this.player;
    const left = state.left;
    const right = state.right;
    const up = state.up;
    const down = state.down;

    // Climbing takes precedence over walking — Donkey Kong rule.
    if ((up || down) && this.isOverLadder(p)) {
      p.onLadder = true;
      p.vx = 0;
      p.vy = up ? -PLAYER_CLIMB_SPEED : PLAYER_CLIMB_SPEED;
    } else if (p.onLadder && !(up || down) && this.isOverLadder(p)) {
      // Holding still on a ladder — stop vertical motion, no gravity.
      p.vy = 0;
    } else {
      p.onLadder = false;
      // Walk only when on ground.
      if (p.onGround) {
        if (left && !right) {
          p.vx = -PLAYER_WALK_SPEED;
          p.facing = -1;
        } else if (right && !left) {
          p.vx = PLAYER_WALK_SPEED;
          p.facing = 1;
        } else {
          p.vx = 0;
        }
      }
    }

    // Jump — only from ground, only when not climbing.
    if (state.justPressed.has('fireA') && p.onGround && !p.onLadder) {
      p.vy = PLAYER_JUMP_VY;
      p.onGround = false;
      this.ctx.audio.sfx('jump');
    }
  }

  update(_dt: number): void {
    this.tickCount += 1;

    if (this.phase === 'game-over') return;

    if (this.phase === 'crashed') {
      this.crashFlash -= 1;
      if (this.crashFlash <= 0) {
        if (this.lives > 0) {
          this.respawn();
        } else {
          this.finalScore = this.score;
          this.finalLevel = this.level;
          this.phase = 'game-over';
        }
      }
      return;
    }

    if (this.phase === 'level-up') {
      this.levelUpTicks -= 1;
      if (this.levelUpTicks <= 0) {
        this.startLevel(this.level + 1);
      }
      return;
    }

    this.updatePlayer();
    this.updateCrates();
    this.updateCrane();
    this.checkLevelWin();
    this.checkCollisions();

    // Survival tick: small score accrual just for staying alive +
    // engaged. Climbs higher = bigger reward, so being at a higher row
    // pays more than camping the bottom platform. The crate-jump bonus
    // and level-clear bonus sit on top of this.
    const row = this.currentPlayerRow();
    if (row >= 0) {
      // Row 0 (bottom) = 1 pt every 12 ticks (~5/s); top row pays 6x.
      const reward = 1 + row;
      if (this.tickCount % 12 === 0) {
        this.score += reward;
      }
    }
  }

  /** Which platform row the player's feet are currently on (or just
   *  above by a couple px). Returns -1 if airborne / climbing. */
  private currentPlayerRow(): number {
    const p = this.player;
    if (!p.onGround) return -1;
    const feetY = p.y + PLAYER_H;
    for (let r = 0; r < ROWS; r++) {
      if (Math.abs(feetY - platformY(r)) < 1.5) return r;
    }
    return -1;
  }

  render(g: CanvasRenderingContext2D): void {
    // Full canvas background — slightly different tone in the gutters
    // vs the playfield to give the column a subtle inset feel.
    g.fillStyle = GUTTER_BG;
    g.fillRect(0, 0, WIDTH, HEIGHT);
    g.fillStyle = FIELD_BG;
    g.fillRect(PLAY_X0, 0, PLAY_W, HEIGHT);

    this.renderGutterChrome(g);
    this.renderScaffolding(g);
    this.renderLadders(g);
    this.renderCrane(g);
    this.renderCrates(g);
    this.renderPlayer(g);
    this.renderHUD(g);

    if (this.phase === 'crashed') {
      // Brief white flash on death.
      if (this.crashFlash > CRASH_FLASH_TICKS - 5) {
        g.fillStyle = FLASH_HIT;
        g.fillRect(PLAY_X0, 0, PLAY_W, HEIGHT);
      }
    }
    if (this.phase === 'level-up') {
      this.renderLevelBanner(g);
    }
    if (this.phase === 'game-over') {
      this.renderGameOver(g);
    }
  }

  resize(): void {
    // Fixed canvas — no responsive layout. The cabinet runtime resizes
    // the canvas backing store; render() uses constants.
  }

  pause(): void {
    // No persistent audio loop yet — nothing to mute. When the audio
    // bus actually fires SFX this method will need to stop loops.
  }
  resume(): void {
    // Mirror of pause(); no-op until audio is wired.
  }
  teardown(): void {
    this.crates = [];
  }

  getScore(): number {
    return Math.floor(this.phase === 'game-over' ? this.finalScore : this.score);
  }
  getFinalScore(): number {
    // Mid-run callers (validators, headless tests) read the live score;
    // game-over freezes it to `finalScore` so post-mortem display is
    // stable even as `score` accrues nothing further.
    return Math.floor(
      this.phase === 'game-over' ? this.finalScore : this.score
    );
  }
  isGameOver(): boolean {
    return this.phase === 'game-over';
  }
  shouldRestart(): boolean {
    return this.phase === 'game-over' && this.restartArmed;
  }

  // -- Internal state machine ------------------------------------------

  private makePlayer(): Player {
    return {
      x: PLAY_X0 + PLAY_W / 2 - PLAYER_W / 2,
      y: platformY(0) - PLAYER_H,
      vx: 0,
      vy: 0,
      onGround: true,
      onLadder: false,
      facing: 1,
      invuln: RESPAWN_INVULN,
      landDust: 0,
      ridingCrate: null,
    };
  }

  private respawn(): void {
    this.player = this.makePlayer();
    this.crates = [];
    this.craneSpawnTimer = CRANE_BASE_INTERVAL;
    this.craneWarnTimer = 0;
    this.phase = 'playing';
  }

  private startLevel(level: number): void {
    this.level = level;
    this.crates = [];
    this.craneSpawnTimer = this.craneIntervalForLevel();
    this.craneWarnTimer = 0;
    this.rollNextCrateTier();
    // Drop the player back to the bottom for the next climb.
    this.player.x = PLAY_X0 + PLAY_W / 2 - PLAYER_W / 2;
    this.player.y = platformY(0) - PLAYER_H;
    this.player.vx = 0;
    this.player.vy = 0;
    this.player.onGround = true;
    this.player.onLadder = false;
    this.player.invuln = RESPAWN_INVULN;
    this.player.ridingCrate = null;
    this.phase = 'playing';
  }

  private craneIntervalForLevel(): number {
    const interval =
      CRANE_BASE_INTERVAL - (this.level - 1) * CRANE_INTERVAL_LEVEL_DROP;
    return Math.max(CRANE_MIN_INTERVAL, interval);
  }

  // -- Player physics --------------------------------------------------

  private updatePlayer(): void {
    const p = this.player;
    if (p.invuln > 0) p.invuln -= 1;
    if (p.landDust > 0) p.landDust -= 1;

    // Mid-air crate ride: player position is locked to the crate.
    if (p.ridingCrate) {
      // If the crate has settled on a platform, dismount.
      if (p.ridingCrate.vy === 0) {
        p.y = p.ridingCrate.y - PLAYER_H;
        p.x = p.ridingCrate.x + p.ridingCrate.vx > PLAY_X1 - PLAYER_W
          ? PLAY_X1 - PLAYER_W
          : Math.max(PLAY_X0, p.ridingCrate.x + 4);
        p.vx = 0;
        p.vy = 0;
        p.onGround = true;
        p.ridingCrate = null;
      } else {
        // Stick to the top of the carrying crate.
        p.x = p.ridingCrate.x + p.ridingCrate.w() / 2 - PLAYER_W / 2;
        p.y = p.ridingCrate.y - PLAYER_H;
      }
      return;
    }

    if (p.onLadder) {
      // No gravity while climbing.
      p.x += p.vx;
      p.y += p.vy;
      // Clamp y so the player can't climb above the highest platform's
      // ladder top — that platform IS the top.
      const minY = platformY(ROWS - 1) - PLAYER_H;
      if (p.y < minY) p.y = minY;
      // If they walk off the ladder column laterally (input flips), stop
      // climbing on the next input frame; for now just clamp x to the
      // ladder centerline so they don't fall off mid-rung.
      const rowFrom = this.ladderRowUnder(p);
      if (rowFrom >= 0) {
        const lx = ladderX(rowFrom);
        p.x = lx - PLAYER_W / 2;
      }
      // Snap to a platform when the player's feet reach a row.
      const settledRow = this.feetSettledRow(p);
      if (settledRow >= 0 && p.vy === 0) {
        p.onLadder = false;
        p.onGround = true;
        p.y = platformY(settledRow) - PLAYER_H;
      }
      return;
    }

    // Apply gravity if airborne.
    if (!p.onGround) {
      p.vy += GRAVITY;
      if (p.vy > 6) p.vy = 6;
    }

    p.x += p.vx;
    p.y += p.vy;

    // Horizontal clamp to the playfield walls.
    if (p.x < PLAY_X0) p.x = PLAY_X0;
    if (p.x > PLAY_X1 - PLAYER_W) p.x = PLAY_X1 - PLAYER_W;

    // Land on a platform if we've crossed a platform's top while falling.
    const landed = this.tryLandOnPlatform(p);
    if (landed) {
      if (!p.onGround) {
        p.landDust = PLAYER_LAND_DUST_TICKS;
        this.ctx.audio.sfx('land');
      }
      p.onGround = true;
      p.vy = 0;
    } else {
      p.onGround = false;
    }

    // Fall off the bottom = death (rare — only possible if the bottom
    // platform develops a gap in a future story; today the bottom is
    // solid wall-to-wall).
    if (p.y > HEIGHT) {
      this.kill();
    }
  }

  /** True if the player is currently overlapping a ladder column at a
   *  height that allows climbing. */
  private isOverLadder(p: Player): boolean {
    return this.ladderRowUnder(p) >= 0;
  }

  /** Returns the ladder row-index (0..ROWS-2) the player is currently
   *  positioned to climb, or -1 if not on any ladder. */
  private ladderRowUnder(p: Player): number {
    const cx = p.x + PLAYER_W / 2;
    const feetY = p.y + PLAYER_H;
    for (let row = 0; row < ROWS - 1; row++) {
      const lx = ladderX(row);
      const topY = platformY(row + 1);
      const botY = platformY(row);
      if (
        Math.abs(cx - lx) < LADDER_W / 2 + 2 &&
        feetY >= topY - 1 &&
        feetY <= botY + 1
      ) {
        return row;
      }
    }
    return -1;
  }

  /** While climbing, snap to a platform if feet line up with a row top. */
  private feetSettledRow(p: Player): number {
    const feetY = p.y + PLAYER_H;
    for (let row = 0; row < ROWS; row++) {
      const py = platformY(row);
      if (Math.abs(feetY - py) < 1.5) return row;
    }
    return -1;
  }

  private tryLandOnPlatform(p: Player): boolean {
    if (p.vy < 0) return false;
    const feetY = p.y + PLAYER_H;
    for (let row = 0; row < ROWS; row++) {
      const py = platformY(row);
      if (feetY >= py && feetY <= py + Math.max(1, p.vy + 1)) {
        // Snap.
        p.y = py - PLAYER_H;
        return true;
      }
    }
    return false;
  }

  // -- Crates ----------------------------------------------------------

  private updateCrates(): void {
    for (const c of this.crates) {
      // Gravity.
      c.vy += CRATE_FALL_GRAVITY;
      if (c.vy > CRATE_MAX_VY) c.vy = CRATE_MAX_VY;
      c.x += c.vx;
      c.y += c.vy;

      // Bounce off platforms. The bottom platform (row 0) is the
      // player's spawn deck — crates do NOT bounce off it; they pass
      // through and exit the bottom of the playfield, where they're
      // scored as "the player survived this one." This gives every
      // crate a way to resolve regardless of where the player is, and
      // keeps the bottom platform feeling like the player's safe deck.
      const cw = c.w();
      const ch = c.h();
      const cBottom = c.y + ch;
      for (let row = 1; row < ROWS; row++) {
        const py = platformY(row);
        if (
          c.vy > 0 &&
          cBottom >= py &&
          cBottom <= py + c.vy + 1 &&
          c.x + cw > PLAY_X0 &&
          c.x < PLAY_X1
        ) {
          const nextVy =
            CRATE_BOUNCE_VY[c.tier] * Math.pow(CRATE_BOUNCE_DAMPING, c.bounces);
          if (nextVy > CRATE_MIN_BOUNCE_VY) {
            // Bounce too weak — let the crate pass through. It'll fall
            // off the bottom of the playfield and exit, scoring as
            // "survived." Don't break — let the loop continue.
            continue;
          }
          c.y = py - ch;
          c.vy = nextVy;
          c.bounces += 1;
          this.ctx.audio.sfx('crate-bounce');
          // L-shape: random chance to flip horizontal direction at a
          // bounce — the "chaotic" feel.
          if (c.tier === 2 && this.ctx.random.next() < 0.4) {
            c.vx = -c.vx;
          }
          // Scoring: every time a crate bounces off a platform that's
          // BELOW the player's current row, the player earns the
          // tier's score. This is the climb-Donkey-Kong incentive —
          // climb higher to be "above" more crate bounces, which pays
          // you for the risk. Tracked per-crate via `scored` so a single
          // crate doesn't double-pay on multiple bounces.
          if (!c.scored) {
            const playerRow = this.currentPlayerRow();
            if (playerRow > row) {
              c.scored = true;
              this.score += CRATE_SCORE[c.tier];
            }
          }
          break;
        }
      }

      // Bounce off playfield walls.
      if (c.x < PLAY_X0) {
        c.x = PLAY_X0;
        c.vx = Math.abs(c.vx);
      } else if (c.x + cw > PLAY_X1) {
        c.x = PLAY_X1 - cw;
        c.vx = -Math.abs(c.vx);
      }
    }

    // Age out crates that have fallen below the playfield. Each crate
    // that exits the bottom without ever scoring (via a player-above
    // bounce) credits the tier's full score — "the player survived
    // this crate." Crates scored on bounce don't double-count.
    const remaining: Crate[] = [];
    for (const c of this.crates) {
      if (c.y >= HEIGHT + 20) {
        if (!c.scored) {
          c.scored = true;
          this.score += CRATE_SCORE[c.tier];
        }
        continue;
      }
      remaining.push(c);
    }
    this.crates = remaining;
  }

  private spawnCrate(tier: CrateTier): Crate {
    const w = CRATE_W[tier];
    const h = CRATE_H[tier];
    // Spawn anywhere across the playfield top, biased slightly toward
    // center so the bottom row sees roughly even crate traffic.
    const spawnX =
      PLAY_X0 + 10 + this.ctx.random.next() * (PLAY_W - 20 - w);
    const dir = this.ctx.random.next() < 0.5 ? -1 : 1;
    return makeCrate(
      spawnX,
      40, // just below the crane
      dir * CRATE_HORIZONTAL_VX[tier],
      0,
      tier,
      w,
      h,
      this.tickCount
    );
  }

  private rollNextCrateTier(): void {
    // Per-level probability mix. Square is always common; long appears
    // from level 2; L-shape ramps up with level.
    const r = this.ctx.random.next();
    if (this.level === 1) {
      this.nextCrateTier = r < 0.85 ? 0 : 1;
    } else if (this.level === 2) {
      this.nextCrateTier = r < 0.55 ? 0 : r < 0.85 ? 1 : 2;
    } else {
      // From level 3 on, L-shape is meaningfully common.
      this.nextCrateTier = r < 0.4 ? 0 : r < 0.7 ? 1 : 2;
    }
  }

  // -- Crane -----------------------------------------------------------

  private updateCrane(): void {
    this.craneSpawnTimer -= 1;
    // Fire the crane-spawn warble ONCE when we cross into the warn
    // window (edge-trigger). Without the equality, the SFX would re-fire
    // every tick until spawn, which would lawnmower the audio bus.
    if (this.craneSpawnTimer === CRANE_WARN_TICKS) {
      this.ctx.audio.sfx('crane-spawn');
    }
    if (this.craneSpawnTimer <= CRANE_WARN_TICKS) {
      this.craneWarnTimer = CRANE_WARN_TICKS - this.craneSpawnTimer;
    }
    if (this.craneSpawnTimer <= 0) {
      this.crates.push(this.spawnCrate(this.nextCrateTier));
      this.rollNextCrateTier();
      this.craneSpawnTimer = this.craneIntervalForLevel();
      this.craneWarnTimer = 0;
    }
  }

  // -- Level transitions -----------------------------------------------

  private checkLevelWin(): void {
    // Reaching the top platform = level win. Player must be standing on
    // the top platform (onGround at the topmost row).
    const p = this.player;
    if (!p.onGround) return;
    if (p.ridingCrate) return;
    const feetY = p.y + PLAYER_H;
    if (Math.abs(feetY - platformY(ROWS - 1)) < 1.5) {
      this.score += 100 * this.level;
      this.phase = 'level-up';
      this.levelUpTicks = 70;
      this.ctx.audio.sfx('level-up');
    }
  }

  // -- Collisions ------------------------------------------------------

  private checkCollisions(): void {
    const p = this.player;
    if (p.invuln > 0) return;
    if (p.ridingCrate) return;

    for (const c of this.crates) {
      const cw = c.w();
      const ch = c.h();
      // Player AABB.
      const pLeft = p.x;
      const pRight = p.x + PLAYER_W;
      const pTop = p.y;
      const pBottom = p.y + PLAYER_H;
      // Crate AABB.
      const cLeft = c.x;
      const cRight = c.x + cw;
      const cTop = c.y;
      const cBottom = c.y + ch;

      const overlap =
        pLeft < cRight && pRight > cLeft && pTop < cBottom && pBottom > cTop;
      if (!overlap) {
        // Scoring is handled in updateCrates() on bounce events while
        // the player is at a higher row. Collision branch only deals
        // with overlap → catch-or-die.
        continue;
      }

      // Mid-air catch: if the player is moving up and the player's TOP
      // is roughly at the crate's BOTTOM, latch on.
      if (
        p.vy <= CATCH_LATCH_VY_MAX &&
        pTop <= cBottom &&
        pTop >= cTop &&
        Math.abs(p.x + PLAYER_W / 2 - (c.x + cw / 2)) < cw / 2 + PLAYER_W / 2
      ) {
        p.ridingCrate = c;
        p.vx = 0;
        p.vy = 0;
        p.onGround = false;
        return;
      }

      // Otherwise — death.
      this.kill();
      return;
    }
  }

  private kill(): void {
    this.lives -= 1;
    this.crashFlash = CRASH_FLASH_TICKS;
    this.phase = 'crashed';
    this.ctx.audio.sfx('crate-hit');
  }

  // -- Rendering -------------------------------------------------------

  private renderGutterChrome(g: CanvasRenderingContext2D): void {
    // Left gutter — vertical scaffold beam down the inner edge so the
    // playfield feels framed.
    g.fillStyle = SCAFFOLD_DARK;
    g.fillRect(PLAY_X0 - 4, 0, 3, HEIGHT);
    g.fillStyle = SCAFFOLD;
    g.fillRect(PLAY_X0 - 4, 0, 1, HEIGHT);

    // Right gutter — mirror beam.
    g.fillStyle = SCAFFOLD_DARK;
    g.fillRect(PLAY_X1 + 1, 0, 3, HEIGHT);
    g.fillStyle = SCAFFOLD;
    g.fillRect(PLAY_X1 + 3, 0, 1, HEIGHT);

    // Right gutter — extended scaffolding beneath the crane, plus
    // hazard stripe at the bottom for industrial texture.
    g.fillStyle = SCAFFOLD_DARK;
    for (let y = 30; y < HEIGHT - 30; y += 30) {
      g.fillRect(PLAY_X1 + 8, y, 24, 2);
    }
    // Hazard stripe along the bottom of the right gutter.
    for (let x = PLAY_X1 + 10; x < WIDTH - 4; x += 8) {
      g.fillStyle = (x / 8) % 2 < 1 ? LADDER : SCAFFOLD_DARK;
      g.fillRect(x, HEIGHT - 8, 6, 4);
    }
  }

  private renderScaffolding(g: CanvasRenderingContext2D): void {
    for (let row = 0; row < ROWS; row++) {
      const y = platformY(row);
      g.fillStyle = SCAFFOLD;
      g.fillRect(PLAY_X0, y, PLAY_W, PLATFORM_THICKNESS);
      g.fillStyle = SCAFFOLD_DARK;
      g.fillRect(PLAY_X0, y + PLATFORM_THICKNESS - 1, PLAY_W, 1);
    }
  }

  private renderLadders(g: CanvasRenderingContext2D): void {
    for (let row = 0; row < ROWS - 1; row++) {
      const lx = ladderX(row);
      const topY = platformY(row + 1);
      const botY = platformY(row);
      // Stiles.
      g.fillStyle = LADDER;
      g.fillRect(lx - LADDER_W / 2, topY, 2, botY - topY);
      g.fillRect(lx + LADDER_W / 2 - 2, topY, 2, botY - topY);
      // Rungs.
      g.fillStyle = LADDER_RUNG;
      for (let ry = topY + 4; ry < botY - 2; ry += 6) {
        g.fillRect(lx - LADDER_W / 2 + 2, ry, LADDER_W - 4, 1);
      }
    }
  }

  private renderCrane(g: CanvasRenderingContext2D): void {
    // Sits above the top platform (platformY(ROWS-1) ≈ 40).
    const topY = platformY(ROWS - 1);
    // Cable extending down to track height.
    g.fillStyle = CRANE_CABLE;
    g.fillRect(PLAY_X0 + PLAY_W / 2 - 1, 0, 2, topY - 2);
    // Crane body / track running across the top.
    g.fillStyle = CRANE_BODY;
    g.fillRect(PLAY_X0, 8, PLAY_W, 12);
    // Warning light — blinks during the warn-window before a spawn.
    const lightOn =
      this.craneWarnTimer > 0 &&
      Math.floor(this.tickCount / 4) % 2 === 0;
    g.fillStyle = lightOn ? CRANE_LIGHT_ON : CRANE_LIGHT_OFF;
    g.fillRect(PLAY_X0 + 6, 10, 6, 6);
  }

  private renderCrates(g: CanvasRenderingContext2D): void {
    for (const c of this.crates) {
      const cw = c.w();
      const ch = c.h();
      const fill =
        c.tier === 0 ? CRATE_SQUARE : c.tier === 1 ? CRATE_LONG : CRATE_L;
      const dark =
        c.tier === 0
          ? CRATE_SQUARE_DARK
          : c.tier === 1
            ? CRATE_LONG_DARK
            : CRATE_L_DARK;
      g.fillStyle = fill;
      g.fillRect(c.x, c.y, cw, ch);
      // Dark inner shadow on the bottom + right edge for a chunky feel.
      g.fillStyle = dark;
      g.fillRect(c.x, c.y + ch - 2, cw, 2);
      g.fillRect(c.x + cw - 2, c.y, 2, ch);
      // Outline the player's carried crate so they can spot it.
      if (this.player.ridingCrate === c) {
        g.strokeStyle = CRATE_CARRIED_OUTLINE;
        g.lineWidth = 1;
        g.strokeRect(c.x + 0.5, c.y + 0.5, cw - 1, ch - 1);
      }
    }
  }

  private renderPlayer(g: CanvasRenderingContext2D): void {
    const p = this.player;
    // Invuln blink — flicker on alternate 4-tick windows.
    if (p.invuln > 0 && p.invuln % 8 < 4) return;

    // Land dust puff.
    if (p.landDust > 0) {
      g.fillStyle = PLAYER_LAND_DUST;
      g.fillRect(p.x - 2, p.y + PLAYER_H - 2, PLAYER_W + 4, 2);
    }

    // Body.
    g.fillStyle = PLAYER_BODY;
    g.fillRect(p.x, p.y + 4, PLAYER_W, PLAYER_H - 4);
    // Outline along the trailing edge to convey facing.
    g.fillStyle = PLAYER_OUTLINE;
    if (p.facing === 1) {
      g.fillRect(p.x, p.y + 4, 1, PLAYER_H - 4);
    } else {
      g.fillRect(p.x + PLAYER_W - 1, p.y + 4, 1, PLAYER_H - 4);
    }
    // Head.
    g.fillStyle = PLAYER_HEAD;
    g.fillRect(p.x + 2, p.y, PLAYER_W - 4, 4);
  }

  private renderHUD(g: CanvasRenderingContext2D): void {
    // Left gutter HUD — score, lives, level, next-crate preview.
    g.font = '8px ui-monospace, monospace';
    g.textAlign = 'left';
    g.textBaseline = 'top';

    const x0 = 6;
    let y = 8;

    g.fillStyle = HUD_LABEL;
    g.fillText('SCORE', x0, y);
    y += 10;
    g.fillStyle = HUD_SCORE;
    g.font = 'bold 12px ui-monospace, monospace';
    g.fillText(String(Math.floor(this.score)).padStart(6, '0'), x0, y);
    y += 18;

    g.font = '8px ui-monospace, monospace';
    g.fillStyle = HUD_LABEL;
    g.fillText('LEVEL', x0, y);
    y += 10;
    g.fillStyle = HUD_LEVEL;
    g.font = 'bold 12px ui-monospace, monospace';
    g.fillText(String(this.level).padStart(2, '0'), x0, y);
    y += 18;

    g.font = '8px ui-monospace, monospace';
    g.fillStyle = HUD_LABEL;
    g.fillText('LIVES', x0, y);
    y += 10;
    g.fillStyle = HUD_LIVES;
    for (let i = 0; i < this.lives; i++) {
      g.fillRect(x0 + i * 8, y, 5, 7);
    }
    y += 16;

    // Next-crate preview.
    g.fillStyle = HUD_LABEL;
    g.fillText('NEXT', x0, y);
    y += 10;
    this.renderNextCratePreview(g, x0, y);

    // Crane-active alert near the bottom of the gutter.
    if (this.craneWarnTimer > 0) {
      g.fillStyle = HUD_ALERT;
      g.font = 'bold 8px ui-monospace, monospace';
      if (Math.floor(this.tickCount / 6) % 2 === 0) {
        g.fillText('! CRANE', x0, HEIGHT - 16);
      }
    }
  }

  private renderNextCratePreview(
    g: CanvasRenderingContext2D,
    x: number,
    y: number
  ): void {
    const tier = this.nextCrateTier;
    const w = CRATE_W[tier];
    const h = CRATE_H[tier];
    const fill =
      tier === 0 ? CRATE_SQUARE : tier === 1 ? CRATE_LONG : CRATE_L;
    g.fillStyle = fill;
    g.fillRect(x, y, w, h);
  }

  private renderLevelBanner(g: CanvasRenderingContext2D): void {
    g.textAlign = 'center';
    g.textBaseline = 'middle';
    g.font = 'bold 16px ui-monospace, monospace';
    g.fillStyle = HUD_SCORE;
    g.fillText(`LEVEL ${this.level} CLEAR`, WIDTH / 2, HEIGHT / 2 - 8);
    g.font = '10px ui-monospace, monospace';
    g.fillStyle = HUD_LABEL;
    g.fillText(`+${100 * this.level} CLIMB BONUS`, WIDTH / 2, HEIGHT / 2 + 10);
    g.textBaseline = 'top';
    g.textAlign = 'left';
  }

  private renderGameOver(g: CanvasRenderingContext2D): void {
    // Full-canvas scrim — dim the frozen playfield + HUD behind so the
    // banner reads cleanly. Matches the Stardust / Maze Patrol pattern
    // for cabinet-wide consistency on end screens.
    g.fillStyle = 'rgba(0, 0, 0, 0.72)';
    g.fillRect(0, 0, WIDTH, HEIGHT);

    g.textAlign = 'center';
    g.textBaseline = 'middle';
    g.font = 'bold 22px ui-monospace, monospace';
    g.fillStyle = HUD_ALERT;
    g.fillText('GAME OVER', WIDTH / 2, HEIGHT / 2 - 22);

    g.font = '11px ui-monospace, monospace';
    g.fillStyle = HUD_SCORE;
    g.fillText(
      `SCORE  ${String(Math.floor(this.finalScore)).padStart(6, '0')}`,
      WIDTH / 2,
      HEIGHT / 2 + 2
    );
    g.fillStyle = HUD_LEVEL;
    g.fillText(`LEVEL  ${this.finalLevel}`, WIDTH / 2, HEIGHT / 2 + 18);

    g.font = '10px ui-monospace, monospace';
    g.fillStyle = HUD_LABEL;
    if (Math.floor(this.tickCount / 30) % 2 === 0) {
      g.fillText('PRESS Z TO RESTART', WIDTH / 2, HEIGHT / 2 + 44);
    }
    g.textBaseline = 'top';
    g.textAlign = 'left';
  }
}

// --- Helpers ----------------------------------------------------------

/** Internal Crate factory — carries cached w/h getters so render +
 *  collision code doesn't repeat the tier->size lookup. */
function makeCrate(
  x: number,
  y: number,
  vx: number,
  vy: number,
  tier: CrateTier,
  width: number,
  height: number,
  born: number
): Crate {
  return {
    x,
    y,
    vx,
    vy,
    tier,
    scored: false,
    born,
    bounces: 0,
    w: () => width,
    h: () => height,
  };
}
